import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pagination } from "./AdminUsers";
import { toast } from "sonner";
import { Eye, ShieldOff, Ban, Lock, ShieldX, Star, ShieldCheck, X, Search } from "lucide-react";

const ACTIONS = [
  { v: "monitor", l: "Monitor", icon: Eye, cls: "border-white/10 text-white hover:bg-white/10" },
  { v: "restrict_certificates", l: "Restrict Certs", icon: ShieldOff, cls: "border-amber-500/40 text-amber-400 hover:bg-amber-500/10" },
  { v: "restrict_recommendations", l: "Restrict Recs", icon: ShieldOff, cls: "border-amber-500/40 text-amber-400 hover:bg-amber-500/10" },
  { v: "freeze_verification", l: "Freeze Verify", icon: Lock, cls: "border-orange-500/40 text-orange-400 hover:bg-orange-500/10" },
  { v: "temp_suspend", l: "Temp Suspend", icon: Ban, cls: "border-ec-error/40 text-ec-error hover:bg-ec-error/10" },
  { v: "permanent_deactivate", l: "Deactivate", icon: ShieldX, cls: "border-ec-error/40 text-ec-error hover:bg-ec-error/10" },
  { v: "mark_trusted", l: "Mark Trusted", icon: Star, cls: "border-ec-emerald/40 text-ec-emerald hover:bg-ec-emerald/10" },
  { v: "whitelist", l: "Whitelist", icon: ShieldCheck, cls: "border-ec-emerald/40 text-ec-emerald hover:bg-ec-emerald/10" },
  { v: "close", l: "Close", icon: X, cls: "border-white/10 text-zinc-400 hover:bg-white/10" },
];

const TIER_CLS = {
  Low: "bg-ec-emerald/10 text-ec-emerald border-ec-emerald/40",
  Medium: "bg-amber-500/10 text-amber-400 border-amber-500/40",
  High: "bg-orange-500/10 text-orange-400 border-orange-500/40",
  Critical: "bg-ec-error/20 text-ec-error border-ec-error/40",
};

export default function TrustSafetyAlerts() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [riskLevel, setRiskLevel] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [skip, setSkip] = useState(0);
  const limit = 25;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/trust-safety/alerts", {
        params: { risk_level: riskLevel, target_role: targetRole, status_filter: statusFilter, skip, limit },
      });
      setRows(data.rows);
      setTotal(data.total);
    } catch (e) {}
  }, [riskLevel, targetRole, statusFilter, skip]);
  useEffect(() => { load(); }, [load]);

  const decide = async (alertId, action) => {
    let note = "";
    if (["restrict_certificates", "restrict_recommendations", "freeze_verification", "temp_suspend", "permanent_deactivate", "close"].includes(action)) {
      note = prompt("Add a note (required for enforcement actions):") || "";
      if (!note) return;
    } else {
      note = prompt("Optional note:") || "";
    }
    try {
      await api.post(`/admin/trust-safety/alerts/${alertId}/decision`, { action, note });
      toast.success(`Action recorded: ${action}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <AdminLayout
      subtitle="Module 8 · Trust & Safety"
      title="Alert Queue"
      actions={<div className="text-xs font-mono-plex text-zinc-500">{total.toLocaleString()} alerts</div>}
    >
      <div className="flex flex-wrap gap-3 mb-5" data-testid="ts-alerts-filters">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-sm h-9 px-3 bg-white/5 border border-white/10 text-white text-sm" data-testid="ts-alerts-status">
          <option value="open" className="bg-ec-ink">Open</option>
          <option value="monitoring" className="bg-ec-ink">Monitoring</option>
          <option value="closed" className="bg-ec-ink">Closed</option>
          <option value="all" className="bg-ec-ink">All</option>
        </select>
        <select value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)} className="rounded-sm h-9 px-3 bg-white/5 border border-white/10 text-white text-sm" data-testid="ts-alerts-risk">
          <option value="" className="bg-ec-ink">All risk levels</option>
          <option value="Critical" className="bg-ec-ink">Critical</option>
          <option value="High" className="bg-ec-ink">High</option>
          <option value="Medium" className="bg-ec-ink">Medium</option>
          <option value="Low" className="bg-ec-ink">Low</option>
        </select>
        <select value={targetRole} onChange={(e) => setTargetRole(e.target.value)} className="rounded-sm h-9 px-3 bg-white/5 border border-white/10 text-white text-sm" data-testid="ts-alerts-role">
          <option value="" className="bg-ec-ink">All roles</option>
          <option value="professional" className="bg-ec-ink">Professional</option>
          <option value="organization" className="bg-ec-ink">Organization</option>
          <option value="institution" className="bg-ec-ink">Institution</option>
        </select>
      </div>

      <div className="space-y-3" data-testid="ts-alerts-list">
        {rows.length === 0 && (
          <Card className="rounded-sm border-white/10 bg-white/[0.02]">
            <CardContent className="py-16 text-center text-zinc-500" data-testid="ts-alerts-empty">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No alerts match the current filters.
            </CardContent>
          </Card>
        )}
        {rows.map((a) => {
          const tierCls = TIER_CLS[a.risk_level] || TIER_CLS.Low;
          return (
            <Card key={a.id} className="rounded-sm border-white/10 bg-white/[0.02] hover:border-white/20 transition-colors" data-testid={`ts-alert-${a.id}`}>
              <CardContent className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge className={`${tierCls} border rounded-sm`}>{a.risk_level}</Badge>
                      <Badge className="bg-white/10 border-white/10 rounded-sm font-mono-plex text-[10px]">{a.detector_key}</Badge>
                      <Badge className="bg-white/10 border-white/10 rounded-sm">+{a.score} pts</Badge>
                      <span className="text-[10px] font-mono-plex text-zinc-500">{a.created_at?.slice(0, 16)}</span>
                    </div>
                    <Link to={`/admin/trust-safety/account/${a.target_user_id}`} className="font-display text-lg font-bold text-white hover:text-ec-emerald" data-testid={`ts-alert-target-${a.id}`}>
                      {a.target_name || a.target_ethic_id}
                    </Link>
                    <div className="text-xs text-zinc-500 mt-0.5 font-mono-plex">{a.target_role} · {a.target_ethic_id}</div>
                    <p className="text-sm text-zinc-300 mt-3 leading-relaxed">{a.reason}</p>

                    {/* AI Investigation Summary — RULE-BASED */}
                    <div className="mt-3 p-3 bg-black/30 border-l-2 border-ec-emerald/40 rounded-sm">
                      <div className="eyebrow text-ec-emerald flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-ec-emerald animate-pulse" />
                        Investigation summary · rule-based
                      </div>
                      <div className="text-xs text-zinc-400 mt-2 font-mono-plex">
                        Suggested action · <span className="text-white font-semibold">{a.suggested_action.replace(/_/g, " ")}</span>
                      </div>
                      {a.evidence && Object.keys(a.evidence).length > 0 && (
                        <div className="text-xs text-zinc-500 mt-1 font-mono-plex truncate">
                          Evidence · {Object.entries(a.evidence).map(([k, v]) => `${k}=${v}`).join(" · ")}
                        </div>
                      )}
                    </div>
                  </div>

                  {a.status === "open" && (
                    <div className="flex flex-wrap gap-1.5 max-w-md">
                      {ACTIONS.filter((x) => x.v === a.suggested_action || ["monitor", "close", "whitelist"].includes(x.v)).map((x) => (
                        <ActionBtn key={x.v} action={x} onClick={() => decide(a.id, x.v)} highlight={x.v === a.suggested_action} testid={`ts-act-${x.v}-${a.id}`} />
                      ))}
                    </div>
                  )}
                  {a.status !== "open" && (
                    <div className="text-xs text-zinc-500 font-mono-plex" data-testid={`ts-alert-closed-${a.id}`}>
                      {a.status} · {a.decision || ""}
                    </div>
                  )}
                </div>

                {/* Full actions row (collapsed by default — show 9 enforcement options) */}
                {a.status === "open" && (
                  <details className="mt-4 text-xs">
                    <summary className="cursor-pointer text-zinc-500 hover:text-white">More enforcement options ▾</summary>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {ACTIONS.map((x) => (
                        <ActionBtn key={x.v} action={x} onClick={() => decide(a.id, x.v)} testid={`ts-actall-${x.v}-${a.id}`} />
                      ))}
                    </div>
                  </details>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Pagination total={total} skip={skip} limit={limit} setSkip={setSkip} />
    </AdminLayout>
  );
}

function ActionBtn({ action, onClick, highlight, testid }) {
  const Icon = action.icon;
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={onClick}
      className={`rounded-sm h-7 border gap-1 ${action.cls} ${highlight ? "ring-1 ring-ec-emerald" : ""}`}
      data-testid={testid}
    >
      <Icon className="w-3 h-3" />
      {action.l}
    </Button>
  );
}
